'use client';

import { QrCodeImage } from '@/app/components';
import { Button } from '@/components/ui';
import { logo } from '@/images';
import { cn } from '@/lib';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Download, MailOpen, Wallet } from 'lucide-react';
import Image from 'next/image';
import { useRef, useState } from 'react';
// import { useSelector } from 'react-redux';
import { CommonImage } from '@/components/common';
import { useReactToPrint } from 'react-to-print';

interface TProps {
  bookingId: string;
  eventName: string;
  bookingDateString: string;
  eventTime: string;
  eventTicketType: string;
  venueAddress: string;
  posterUrl: string;
  qrText: string;
  userName: string;
  totalAmount: string;
  bookedAt: string;
  numberOfSeats: number;
  ticketTypeArray: Array<{
    name: string;
    code: string;
    quantity: number;
  }>;
  className?: string;
  encQrObjectText: string;
  onspot?: boolean;
  transactionId: {
    paymentBody: any;
  } | string;
  taxAmount: string;
  bookingAmount: string;
  convenienceAmount: string;
  donationAmount: string;
  specialDiscountAmount: string;
  paymentAmount: string;
  baseTicketAmount: string;
  bookingAmountBeforeGST: string;
  showCode?: boolean;
  slotLabel: string;
}

const Ticket = ({
  bookingId,
  eventName,
  bookingDateString: eventDate,
  eventTicketType,
  numberOfSeats,
  eventTime,
  venueAddress,
  posterUrl,
  userName,
  totalAmount,
  bookedAt,
  ticketTypeArray,
  className,
  encQrObjectText,
  onspot,
  transactionId,
  taxAmount,
  bookingAmount,
  convenienceAmount,
  donationAmount,
  specialDiscountAmount,
  paymentAmount,
  baseTicketAmount,
  bookingAmountBeforeGST,
  showCode = false,
  slotLabel,
}: TProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const printTicket = useReactToPrint({ contentRef });
  const ticketType = ticketTypeArray?.at(0);
  const [isPaymentExpanded, setIsPaymentExpanded] = useState(false);
  const [isBookingChargesExpanded, setIsBookingChargesExpanded] = useState(true);
  const ticketAmount = Number(baseTicketAmount) || Number(transactionId?.paymentBody?.originalAmount) || 0;
  const taxes = Number(taxAmount) || Number(transactionId?.paymentBody?.taxAmount) || 0;
  const bookingfee = Number(bookingAmount) || Number(transactionId?.paymentBody?.bookingAmount || 0);
  const convenienceFee = (Number(convenienceAmount) || Number(transactionId?.paymentBody?.convenienceAmount)) || 0;
  const donation = Number(donationAmount) || Number(transactionId?.paymentBody?.donationAmount) || 0;
  const discount = Number(specialDiscountAmount) || Number(transactionId?.paymentBody?.specialDiscountAmount) || 0;
  const total = Number(paymentAmount) || Number(transactionId?.paymentBody?.paymentAmount) || 0;
  const baseBooking = (Number(bookingAmountBeforeGST) || Number(transactionId?.paymentBody?.bookingAmountBeforeGST || Number(bookingAmount)) || 0);
  const gst = Number((Number(bookingAmountBeforeGST) > 0
    ? Number(bookingAmount) -
    Number(bookingAmountBeforeGST)
    : Number(transactionId?.paymentBody?.bookingAmountBeforeGST) > 0 ? Number(transactionId?.paymentBody?.bookingAmount) - Number(transactionId?.paymentBody?.bookingAmountBeforeGST) : 0)?.toFixed(2));

  return (
    <div
      className={cn(
        'hide-scrollbar flex w-full flex-col gap-3 overflow-scroll',
        className,
      )}
      ref={contentRef}
    >
      <div className='w-full print:px-2'>
        <div className='flex flex-col justify-between gap-4 p-3 px-6 md:flex-row'>
          <div className='flex flex-col items-start gap-2 print:pt-8'>
            {(bookingId && showCode) && (
              <p className='rounded-lg border-2 border-dashed p-2 px-3 text-center text-lg font-bold'>
                {bookingId}
              </p>
            )}
            <p className='text-lg font-bold'>{eventName}</p>
            <p className='text-xs font-semibold text-zinc-600'>
              {venueAddress}
            </p>
            <Button
              className='flex items-center gap-1 rounded-md border p-2 text-sm font-semibold print:hidden'
              variant='outline'
              onClick={printTicket}
            >
              <Download size={16} />
              Download Ticket
            </Button>
          </div>
          {posterUrl && (
            <CommonImage
              src={posterUrl}
              alt={`event ${bookingId}`}
              className='w-full rounded-lg max-h-32 md:max-h-28 md:max-w-36 print:hidden object-center'
              width={140}
              height={100}
            />
          )}
        </div>

        <div className='flex flex-col items-center justify-center gap-4 p-3 px-6 md:flex-row'>
          {encQrObjectText && (
            <QrCodeImage
              value={encQrObjectText}
              size={300}
              className='w-[135px]'
            />
          )}

          <div className='w-full overflow-hidden rounded-lg border'>
            <div className='flex items-center justify-between border-b bg-gray-50 p-3'>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-700'>
                  <strong>{numberOfSeats}</strong> Ticket
                </p>
                <p className='text-primary font-bold uppercase'>
                  {typeof ticketType === 'object'
                    ? `${ticketType?.name} : ${ticketType?.quantity}`
                    : ticketType}
                </p>
              </div>
              <div className='flex flex-row items-center justify-end gap-1'>
                <span className='rounded-sm bg-green-200 px-2 py-1 text-[10px] font-semibold tracking-wide text-green-600'>
                  {onspot ? 'Offline' : 'Online'}
                </span>
              </div>
            </div>
            <div className='grid grid-cols-2 p-3'>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-500'>
                  Date
                </p>
                <p className='text-base font-semibold'>{eventDate}</p>
              </div>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-500'>
                  Time
                </p>
                <p className='text-base font-semibold'>{slotLabel || eventTime}</p>
              </div>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 sm:grid-cols-3 gap-4 p-3 px-6'>
          <div className='flex gap-2'>
            <MailOpen size={16} className='mt-1 text-zinc-500 shrink-0' />
            <div className='flex flex-col'>
              <p className='text-xs font-semibold text-zinc-500'>
                Delivery Name
              </p>
              <p className='text-base font-semibold text-zinc-700'>
                {userName}
              </p>
            </div>
          </div>
          {!onspot && (
            <div className='flex gap-2'>
              <Wallet size={16} className='mt-1 text-zinc-500 shrink-0' />
              <div className='flex flex-col'>
                <p className='text-xs font-semibold text-zinc-500'>
                  Payment
                </p>
                <p className='text-base font-semibold text-zinc-700 uppercase'>
                  {eventTicketType === 'FREE_FCFS'
                    ? 'free'
                    : `₹ ${total}`}
                </p>
              </div>
            </div>
          )}
        </div>

        {(!onspot && total > 0 && eventTicketType !== 'FREE_FCFS') && (
          <div className='px-6 py-2 pb-4'>
            <div className='overflow-hidden rounded-lg border border-dashed bg-zinc-50/50 transition-all'>
              <button
                onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
                className='flex w-full items-center justify-between p-4 text-left outline-none print:hidden'
                type='button'
              >
                <p className='text-sm font-semibold tracking-wider text-zinc-500'>
                  Payment Breakdown
                </p>
                {isPaymentExpanded ? (
                  <ChevronUp size={16} className='text-zinc-500' />
                ) : (
                  <ChevronDown size={16} className='text-zinc-500' />
                )}
              </button>
              <p className='hidden p-4 pb-2 text-sm font-semibold tracking-wider text-zinc-500 print:block'>
                Payment Breakdown
              </p>

              <div
                className={cn(
                  'flex flex-col gap-2 px-4 pb-4 text-sm text-zinc-600',
                  isPaymentExpanded ? 'block' : 'hidden',
                  'print:block'
                )}
              >
                <div className='flex justify-between'>
                  <span>Ticket Amount</span>
                  <span className='font-medium text-zinc-800'>
                    ₹ {ticketAmount}
                  </span>
                </div>
                {taxes > 0 && (
                  <div className='flex justify-between'>
                    <span>Taxes & GST</span>
                    <span className='font-medium text-zinc-800'>
                      ₹ {taxes}
                    </span>
                  </div>
                )}

                {bookingfee > 0 && (
                  <button className='w-full' onClick={() => setIsBookingChargesExpanded(p => !p)}>
                    <div className='flex justify-between'>
                      <span className='flex justify-center items-center gap-1'>
                        Booking Fee (inc. of GST)
                        {isBookingChargesExpanded ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </span>
                      <span className='font-medium text-zinc-800'>
                        ₹ {bookingfee}
                      </span>
                    </div>

                    <div className={cn('pt-1 text-xs', isBookingChargesExpanded ? 'block' : 'hidden')}>
                      <div className='flex justify-between'>
                        <span>Booking Fee</span>
                        <span className='font-medium text-zinc-800'>
                          ₹ {baseBooking}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>GST</span>
                        <span className='font-medium text-zinc-800'>
                          ₹ {gst}
                        </span>
                      </div>
                    </div>
                  </button>
                )}

                {convenienceFee > 0 && (
                  <div className='flex justify-between'>
                    <span>Convenience Fee</span>
                    <span className='font-medium text-zinc-800'>
                      ₹ {convenienceFee}
                    </span>
                  </div>
                )}
                {donation > 0 && (
                  <div className='flex justify-between'>
                    <span>Donation</span>
                    <span className='font-medium text-zinc-800'>
                      ₹ {donation}
                    </span>
                  </div>
                )}
                {discount > 0 && (
                  <div className='flex justify-between text-green-600'>
                    <span>Discount</span>
                    <span className='font-medium'>
                      - ₹ {discount}
                    </span>
                  </div>
                )}
                <div className='my-2 border-t border-dashed border-zinc-300' />
                <div className='flex justify-between text-base font-bold text-zinc-900'>
                  <span>Total Paid</span>
                  <span>₹ {total}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='border-t p-6'>
          <div className='flex flex-col items-center justify-between gap-2 md:flex-row'>
            <div className='flex flex-col'>
              <p className='text-center text-xs font-semibold text-zinc-500 md:text-left'>
                Purchased on
              </p>
              <p className='text-sm font-semibold text-zinc-700'>
                {format(bookedAt, 'dd LLL yyyy h:m a')}
              </p>
            </div>
            <div>
              <Image
                alt='Greet Fydo Logo'
                aria-label='Greet Fydo Logo'
                className='mb-1 h-6 w-auto sm:h-8'
                src={logo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
